/**
 * Substitutions — Page "Exceptions" (v10.1)
 *
 * Single place to manage exceptional/short-range overrides on top of the
 * permanent vehicle configuration. The page lists every active or upcoming
 * ``tour_assignments`` with ``kind="override"`` and lets the dispatcher
 * create / edit / delete them.
 *
 * Source of truth for the permanent affectation = the vehicle's primary_*
 * fields (page "Bus"). This page deals ONLY with daily exceptions:
 *   • chauffeur absent
 *   • bus en panne
 *   • permutation ponctuelle
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageShell, PageHeader, ActionButton } from "@/components/ds";
import LocationPicker from "@/components/LocationPicker";
import {
  Plus, X, Trash2, RefreshCw, AlertCircle, Calendar,
  Bus, User as UserIcon, Loader2, ArrowRight, ShieldAlert, History,
  MapPin, Clock,
} from "lucide-react";

const STATUS_META = {
  active: { label: "ACTIVE", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  upcoming: { label: "À VENIR", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  past: { label: "PASSÉE", cls: "bg-zinc-100 text-zinc-500 border-zinc-300" },
};

function statusFor(row) {
  const today = new Date().toISOString().slice(0, 10);
  if (row.end_date && row.end_date < today) return "past";
  if (row.start_date && row.start_date > today) return "upcoming";
  return "active";
}

/**
 * Format a start/end datetime pair into a compact string for the
 * Exceptions table. Falls back gracefully when only the date part
 * is available or when `all_day` is set.
 */
function formatDateTimeRange(startISO, endISO, allDay) {
  if (!startISO) return "—";
  const sDate = String(startISO).slice(0, 10);
  const eDate = String(endISO || "").slice(0, 10);
  if (allDay) {
    return eDate && eDate !== sDate ? `${sDate} → ${eDate} (24h)` : `${sDate} (24h)`;
  }
  const sTime = String(startISO).slice(11, 16);
  const eTime = String(endISO || "").slice(11, 16);
  if (eDate && eDate !== sDate) return `${sDate} ${sTime} → ${eDate} ${eTime}`;
  return `${sDate} · ${sTime}–${eTime || "?"}`;
}

export default function Substitutions() {
  const [overrides, setOverrides] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("active_and_upcoming");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get("/tour-assignments?kind=override&limit=500"),
        api.get("/vehicles"),
        api.get("/users?role=driver&active_only=true"),
        api.get("/tours").catch(() => ({ data: [] })),
      ]);
      setOverrides(r1.data?.items || r1.data || []);
      setVehicles(r2.data || []);
      setDrivers(r3.data || []);
      setTours(r4.data?.items || r4.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const enriched = useMemo(() => {
    const vById = Object.fromEntries(vehicles.map((v) => [v.id, v]));
    const dById = Object.fromEntries(drivers.map((d) => [d.id, d]));
    const tById = Object.fromEntries(tours.map((t) => [t.tour_id, t]));
    return overrides.map((o) => ({
      ...o,
      vehicle: vById[o.vehicle_id],
      driver: dById[o.driver_id],
      tour: tById[o.tour_id],
      _status: statusFor(o),
    }));
  }, [overrides, vehicles, drivers, tours]);

  const visible = useMemo(() => {
    if (filter === "all") return enriched;
    if (filter === "active") return enriched.filter((r) => r._status === "active");
    if (filter === "upcoming") return enriched.filter((r) => r._status === "upcoming");
    if (filter === "past") return enriched.filter((r) => r._status === "past");
    return enriched.filter((r) => r._status !== "past");
  }, [enriched, filter]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette exception ? L'affectation permanente du véhicule reprendra.")) return;
    try {
      await api.delete(`/tour-assignments/${id}`);
      toast.success("Exception supprimée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Exceptions"
        title="Exceptions ponctuelles"
        subtitle="Exceptions temporaires à la configuration permanente des véhicules."
        actions={(
          <>
            <ActionButton onClick={load} icon={RefreshCw} variant="outline" testId="sub-refresh">
              Rafraîchir
            </ActionButton>
            <ActionButton onClick={() => setDialog({ mode: "new" })} icon={Plus} testId="sub-new">
              Créer une exception
            </ActionButton>
          </>
        )}
      />

      {/* Banner */}
      <div data-testid="sub-banner"
            className="bg-purple-50 border border-purple-300 px-4 py-2 flex items-center gap-3 text-sm">
        <ShieldAlert size={14} className="text-purple-700 shrink-0" />
        <div className="flex-1">
          <strong className="font-display font-black tracking-tight">Exceptions ponctuelles uniquement</strong>
          <span className="text-zinc-700">
            {" "}— ici on gère les <em>chauffeur absent / bus en panne / permutation</em>. La configuration
            permanente Bus → Chauffeur principal → Tournée se fait dans la fiche du
            <a href="/admin/vehicles" className="underline font-bold ml-1 hover:text-purple-700">véhicule</a>.
            Une exception remplace temporairement la configuration du jour ; à la date d&apos;expiration, le bus
            reprend automatiquement sa configuration permanente.
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mt-3 mb-2" data-testid="sub-filters">
        {[
          { key: "active_and_upcoming", label: "Actives & à venir" },
          { key: "active", label: "Actives" },
          { key: "upcoming", label: "À venir" },
          { key: "past", label: "Passées" },
          { key: "all", label: "Tout" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`sub-filter-${f.key}`}
            data-active={filter === f.key ? "1" : undefined}
            className={`px-3 py-1 border text-[10px] font-mono uppercase tracking-widest ${
              filter === f.key
                ? "bg-zinc-900 text-[#FFC60A] border-zinc-900"
                : "bg-white border-zinc-300 hover:border-zinc-900"
            }`}>
            {f.label} {f.key !== "all" && (
              <span className="ml-1 opacity-70">
                {enriched.filter((r) => f.key === "active_and_upcoming" ? r._status !== "past" : r._status === f.key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-zinc-900 bg-white" data-testid="sub-list">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-white">
            <tr className="text-[10px] font-mono uppercase tracking-widest">
              <th className="text-left px-3 py-2">Période</th>
              <th className="text-left px-3 py-2">Bus</th>
              <th className="text-left px-3 py-2">Tournée</th>
              <th className="text-left px-3 py-2">Nouveau chauffeur</th>
              <th className="text-left px-3 py-2">Lieu / Horaire</th>
              <th className="text-left px-3 py-2">Motif</th>
              <th className="text-left px-3 py-2">Statut</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">
                <Loader2 size={14} className="inline animate-spin mr-1" /> Chargement…
              </td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-400">
                Aucune exception {filter === "past" ? "passée" : "active"}.
              </td></tr>
            )}
            {!loading && visible.map((row) => (
              <tr key={row.id} data-testid={`sub-row-${row.id}`} className="hover:bg-zinc-50">
                <td className="px-3 py-2 font-mono text-[12px]">
                  <Calendar size={11} className="inline mr-1 text-zinc-400" />
                  {row.start_datetime
                    ? formatDateTimeRange(row.start_datetime, row.end_datetime, row.all_day)
                    : `${row.start_date} → ${row.end_date || "∞"}`}
                </td>
                <td className="px-3 py-2">
                  <Bus size={11} className="inline mr-1 text-zinc-500" />
                  <strong>{row.vehicle?.plate || row.vehicle?.model || row.vehicle_id?.slice(0, 8)}</strong>
                </td>
                <td className="px-3 py-2 text-zinc-700">
                  {row.tour ? `${row.tour.service_code || "?"}${row.tour.service_letter || ""} · ${row.tour.day_name || ""}` : "—"}
                </td>
                <td className="px-3 py-2">
                  {row.driver ? (
                    <span className="inline-flex items-center gap-1">
                      <UserIcon size={11} className="text-zinc-500" />
                      {row.driver.name}
                    </span>
                  ) : <span className="text-zinc-400 italic">Inchangé</span>}
                </td>
                <td className="px-3 py-2 text-xs" data-testid={`sub-row-loc-${row.id}`}>
                  {(row.poi_name || row.address) ? (
                    <div className="flex items-start gap-1">
                      <MapPin size={11} className="text-purple-700 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate" title={row.poi_name || row.address}>
                          {row.poi_name || row.address}
                        </div>
                        {row.operational_notes && (
                          <div className="text-[10px] text-zinc-600 italic line-clamp-1" title={row.operational_notes}>
                            {row.operational_notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    row.operational_notes ? (
                      <div className="text-[10px] text-zinc-600 italic line-clamp-2" title={row.operational_notes}>
                        {row.operational_notes}
                      </div>
                    ) : <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-zinc-600 text-xs italic">{row.notes || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono font-bold uppercase tracking-widest ${STATUS_META[row._status].cls}`}>
                    {STATUS_META[row._status].label}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setDialog({ mode: "edit", row })}
                    data-testid={`sub-edit-${row.id}`}
                    className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 text-[10px] font-mono uppercase tracking-widest mr-1">
                    Modifier
                  </button>
                  <button
                    onClick={() => remove(row.id)}
                    data-testid={`sub-delete-${row.id}`}
                    className="px-2 py-1 border border-transparent text-zinc-500 hover:text-red-600 hover:border-red-200 text-[10px]">
                    <Trash2 size={11} className="inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <SubstitutionDialog
          dialog={dialog}
          vehicles={vehicles}
          drivers={drivers}
          tours={tours}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }}
        />
      )}
    </PageShell>
  );
}

function SubstitutionDialog({ dialog, vehicles, drivers, tours, onClose, onDone }) {
  const { mode, row } = dialog;
  const [vehicleId, setVehicleId] = useState(row?.vehicle_id || "");
  const [tourId, setTourId] = useState(row?.tour_id || "");
  const [driverId, setDriverId] = useState(row?.driver_id || "");
  const [notes, setNotes] = useState(row?.notes || "");
  // New "Lieu et horaire" section
  const [location, setLocation] = useState({
    location_type: row?.location_type || "none",
    poi_id: row?.poi_id || null,
    poi_name: row?.poi_name || null,
    address: row?.address || null,
    latitude: row?.latitude ?? null,
    longitude: row?.longitude ?? null,
  });
  const today = new Date().toISOString().slice(0, 10);
  // start_datetime / end_datetime — split into date+time inputs in the UI
  const initStart = row?.start_datetime || (row?.start_date ? `${row.start_date}T07:00` : `${today}T07:00`);
  const initEnd = row?.end_datetime || (row?.end_date ? `${row.end_date}T18:00` : `${today}T18:00`);
  const [startDate, setStartDate] = useState(String(initStart).slice(0, 10));
  const [startTime, setStartTime] = useState(String(initStart).slice(11, 16) || "07:00");
  const [endDate, setEndDate] = useState(String(initEnd).slice(0, 10));
  const [endTime, setEndTime] = useState(String(initEnd).slice(11, 16) || "18:00");
  const [allDay, setAllDay] = useState(!!row?.all_day);
  const [operationalNotes, setOperationalNotes] = useState(row?.operational_notes || "");
  const [saving, setSaving] = useState(false);

  // When the vehicle changes, auto-fill the tour from its primary_tour_id
  useEffect(() => {
    if (!vehicleId) return;
    const v = vehicles.find((x) => x.id === vehicleId);
    if (v?.primary_tour_id && !tourId) setTourId(v.primary_tour_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const save = async () => {
    if (!vehicleId) { toast.error("Sélectionnez un bus"); return; }
    if (!tourId)    { toast.error("Sélectionnez la tournée concernée"); return; }
    if (!startDate) { toast.error("Date de début obligatoire"); return; }
    if (!driverId && !notes) {
      toast.warning("Indiquez au moins un nouveau chauffeur OU un motif pour cette exception");
    }
    const effEndDate = endDate || startDate;
    const startDT = allDay ? `${startDate}T00:00` : `${startDate}T${startTime}`;
    const endDT   = allDay ? `${effEndDate}T23:59` : `${effEndDate}T${endTime}`;
    if (endDT < startDT) {
      toast.error("La date/heure de fin doit être postérieure au début");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tour_id: tourId,
        vehicle_id: vehicleId,
        driver_id: driverId || null,
        kind: "override",
        start_date: startDate,
        end_date: effEndDate,
        notes: notes || "Exception ponctuelle",
        // v10.2 — Lieu et horaire
        location_type: location.location_type || "none",
        poi_id: location.poi_id || null,
        poi_name: location.poi_name || null,
        address: location.address || null,
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
        start_datetime: startDT,
        end_datetime: endDT,
        all_day: allDay,
        operational_notes: operationalNotes || null,
        source: "substitutions",
        force: true,
      };
      if (mode === "edit" && row?.id) {
        await api.patch(`/tour-assignments/${row.id}`, payload);
      } else {
        await api.post("/tour-assignments", payload);
      }
      toast.success(mode === "edit" ? "Exception modifiée" : "Exception créée");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const vehicle = vehicles.find((v) => v.id === vehicleId);
  const primaryDriverName = vehicle?.primary_driver_id
    ? drivers.find((d) => d.id === vehicle.primary_driver_id)?.name : null;

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="sub-dialog">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#A855F7] w-full max-w-3xl my-8">
        <header className="px-4 py-3 bg-purple-900 text-white flex items-center gap-3">
          <ShieldAlert size={16} className="text-purple-200" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-purple-200">Exception</div>
            <div className="font-display font-black text-lg tracking-tighter">
              {mode === "edit" ? "Modifier l'exception" : "Nouvelle exception"}
            </div>
          </div>
          <button onClick={onClose} className="text-purple-200 hover:text-white"><X size={18} /></button>
        </header>

        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sub-dialog-form">
          <Field label="Bus concerné">
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              data-testid="sub-dialog-vehicle"
              className="w-full border border-zinc-900 px-3 py-2 bg-white">
              <option value="">— Sélectionner —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.model || v.label || v.plate || v.id}
                </option>
              ))}
            </select>
            {primaryDriverName && (
              <div className="text-[10px] text-zinc-500 mt-1">
                Chauffeur permanent : <strong>{primaryDriverName}</strong>
              </div>
            )}
          </Field>
          <Field label="Tournée concernée">
            <select
              value={tourId}
              onChange={(e) => setTourId(e.target.value)}
              data-testid="sub-dialog-tour"
              className="w-full border border-zinc-900 px-3 py-2 bg-white">
              <option value="">— Sélectionner —</option>
              {tours.map((t) => (
                <option key={t.tour_id} value={t.tour_id}>
                  {`${t.service_code || "?"}${t.service_letter || ""} · ${t.day_name || ""}`.trim()}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Nouveau chauffeur (optionnel)">
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              data-testid="sub-dialog-driver"
              className="w-full border border-zinc-900 px-3 py-2 bg-white">
              <option value="">— Garder le chauffeur permanent —</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Motif">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="sub-dialog-notes"
              placeholder="Ex: Raymond malade, bus en panne…"
              className="w-full border border-zinc-900 px-3 py-2" />
          </Field>
        </div>

        {/* Lieu et horaire de l'exception */}
        <div className="px-4 pb-4" data-testid="sub-dialog-location-section">
          <div className="border-t-2 border-purple-200 pt-4 mt-1">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-purple-700" />
              <h4 className="font-display font-black uppercase text-sm tracking-tight">Lieu et horaire de l&apos;exception</h4>
            </div>

            <Field label="Adresse ou POI">
              <LocationPicker
                value={location}
                onChange={setLocation}
                testIdPrefix="sub-dialog-loc"
              />
            </Field>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Début (date + heure)">
                <div className="flex gap-2">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    data-testid="sub-dialog-start-date"
                    className="flex-1 border border-zinc-900 px-3 py-2" />
                  <input type="time" value={startTime} disabled={allDay}
                    onChange={(e) => setStartTime(e.target.value)}
                    data-testid="sub-dialog-start-time"
                    className="w-32 border border-zinc-900 px-3 py-2 disabled:bg-zinc-100 disabled:text-zinc-400" />
                </div>
              </Field>
              <Field label="Fin (date + heure)">
                <div className="flex gap-2">
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    data-testid="sub-dialog-end-date"
                    className="flex-1 border border-zinc-900 px-3 py-2" />
                  <input type="time" value={endTime} disabled={allDay}
                    onChange={(e) => setEndTime(e.target.value)}
                    data-testid="sub-dialog-end-time"
                    className="w-32 border border-zinc-900 px-3 py-2 disabled:bg-zinc-100 disabled:text-zinc-400" />
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Laissez vide pour une exception d&apos;une seule journée.
                </div>
              </Field>
            </div>

            <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)}
                data-testid="sub-dialog-all-day" className="accent-purple-700" />
              <Clock size={12} className="text-zinc-500" />
              <span className="font-mono text-[11px] uppercase tracking-widest">Toute la journée</span>
            </label>

            <div className="mt-3">
              <Field label="Notes opérationnelles">
                <textarea
                  value={operationalNotes}
                  onChange={(e) => setOperationalNotes(e.target.value)}
                  data-testid="sub-dialog-operational-notes"
                  rows={3}
                  placeholder="Ex: Route fermée, prise en charge déplacée, bus à récupérer au dépôt…"
                  className="w-full border border-zinc-900 px-3 py-2 text-sm" />
              </Field>
            </div>
          </div>
        </div>

        <footer className="border-t border-zinc-900 p-3 flex items-center justify-end gap-2 bg-zinc-50">
          <button onClick={onClose} disabled={saving}
            className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest">
            Annuler
          </button>
          <button onClick={save} disabled={saving}
            data-testid="sub-dialog-save"
            className="px-4 py-1.5 bg-purple-600 text-white border border-purple-800 hover:bg-purple-700 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
            {mode === "edit" ? "Enregistrer" : "Créer l'exception"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
